import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Thermometer, Droplets, Gauge } from 'lucide-react';
import { toast } from 'sonner';

export default function EnvironmentalPage() {
  const [sensors, setSensors] = useState([]);
  const [readings, setReadings] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [newReading, setNewReading] = useState({ temperature: '', humidity: '', pressure: '' });
  const [newSensor, setNewSensor] = useState('');

  const loadSensors = () => api.get('/environment/sensors').then(r => setSensors(r.data)).catch(() => {});
  const loadReadings = (sensorId) => api.get(`/environment/readings${sensorId ? `?sensorId=${sensorId}` : ''}`).then(r => setReadings(r.data)).catch(() => {});

  useEffect(() => { loadSensors(); loadReadings(); }, []);

  const addSensor = async () => {
    if (!newSensor) return;
    try {
      const res = await api.post('/environment/sensors', { name: newSensor });
      loadSensors();
      setNewSensor('');
      toast.success('Senzor přidán');
    } catch { toast.error('Chyba'); }
  };

  const addReading = async () => {
    try {
      await api.post('/environment/readings', {
        sensorId: selectedSensor?.id || '',
        sensorName: selectedSensor?.name || 'Manuální',
        temperature: parseFloat(newReading.temperature) || null,
        humidity: parseFloat(newReading.humidity) || null,
        pressure: parseFloat(newReading.pressure) || null,
      });
      loadReadings(selectedSensor?.id);
      setNewReading({ temperature: '', humidity: '', pressure: '' });
      toast.success('Záznam přidán');
    } catch { toast.error('Chyba'); }
  };

  return (
    <div data-testid="environmental-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Left: Sensors */}
      <div className="ct-panel w-72 flex-shrink-0 flex flex-col">
        <div className="ct-panel-header">Senzory</div>
        <ScrollArea className="flex-1">
          <button
            onClick={() => { setSelectedSensor(null); loadReadings(); }}
            className={`ct-table-row w-full text-left px-3 py-2 text-sm ${!selectedSensor ? 'active' : ''}`}
          >
            Všechny senzory
          </button>
          {sensors.map(s => (
            <button
              key={s.id}
              onClick={() => { setSelectedSensor(s); loadReadings(s.id); }}
              className={`ct-table-row w-full text-left px-3 py-2 text-sm ${selectedSensor?.id === s.id ? 'active' : ''}`}
            >
              <div className="font-medium">{s.name}</div>
              <div className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                {s.status === 'active' ? 'Aktivní' : 'Neaktivní'}
              </div>
            </button>
          ))}
        </ScrollArea>
        <div className="p-2 border-t flex gap-1" style={{ borderColor: 'var(--ct-border)' }}>
          <Input placeholder="Název senzoru" value={newSensor} onChange={e => setNewSensor(e.target.value)} className="h-8 text-sm" />
          <Button onClick={addSensor} size="sm" className="ct-btn-primary h-8 px-2">
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Right: Readings */}
      <div className="flex-1 flex flex-col gap-4">
        {/* New reading form */}
        <div className="ct-panel">
          <div className="ct-panel-header">Nový záznam</div>
          <div className="p-4 flex items-end gap-3">
            <div className="ct-form-field">
              <label className="ct-form-label flex items-center gap-1">
                <Thermometer className="w-3 h-3" /> Teplota (°C)
              </label>
              <Input type="number" step="0.1" value={newReading.temperature} onChange={e => setNewReading({ ...newReading, temperature: e.target.value })} className="h-9 text-sm font-mono w-32" />
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label flex items-center gap-1">
                <Droplets className="w-3 h-3" /> Vlhkost (%)
              </label>
              <Input type="number" step="0.1" value={newReading.humidity} onChange={e => setNewReading({ ...newReading, humidity: e.target.value })} className="h-9 text-sm font-mono w-32" />
            </div>
            <div className="ct-form-field">
              <label className="ct-form-label flex items-center gap-1">
                <Gauge className="w-3 h-3" /> Tlak (hPa)
              </label>
              <Input type="number" step="0.1" value={newReading.pressure} onChange={e => setNewReading({ ...newReading, pressure: e.target.value })} className="h-9 text-sm font-mono w-32" />
            </div>
            <Button data-testid="add-reading-btn" onClick={addReading} className="ct-btn-primary gap-1 h-9">
              <Plus className="w-4 h-4" /> Přidat
            </Button>
          </div>
        </div>

        {/* Readings table */}
        <div className="ct-panel flex-1 flex flex-col">
          <div className="ct-panel-header">Hodnoty</div>
          <ScrollArea className="flex-1">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                  <th className="text-left px-3 py-2" style={{ color: 'var(--ct-text-secondary)' }}>Datum</th>
                  <th className="text-left px-3 py-2" style={{ color: 'var(--ct-text-secondary)' }}>Čas</th>
                  <th className="text-left px-3 py-2" style={{ color: 'var(--ct-text-secondary)' }}>Senzor</th>
                  <th className="text-right px-3 py-2" style={{ color: 'var(--ct-text-secondary)' }}>Teplota (°C)</th>
                  <th className="text-right px-3 py-2" style={{ color: 'var(--ct-text-secondary)' }}>Vlhkost (%)</th>
                  <th className="text-right px-3 py-2" style={{ color: 'var(--ct-text-secondary)' }}>Tlak (hPa)</th>
                </tr>
              </thead>
              <tbody>
                {readings.map(r => {
                  const d = new Date(r.recordedAt);
                  return (
                    <tr key={r.id} className="ct-table-row" style={{ borderBottom: '1px solid var(--ct-border)' }}>
                      <td className="px-3 py-1.5 font-mono text-xs">{d.toLocaleDateString('cs-CZ')}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{d.toLocaleTimeString('cs-CZ')}</td>
                      <td className="px-3 py-1.5">{r.sensorName || '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{r.temperature?.toFixed(1) ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{r.humidity?.toFixed(1) ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{r.pressure?.toFixed(1) ?? '—'}</td>
                    </tr>
                  );
                })}
                {readings.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8" style={{ color: 'var(--ct-text-disabled)' }}>Žádné záznamy</td></tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
